<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <h3 class="text-center">
          <span style="padding: 0 12px;border-left:1px solid;border-right:1px solid;">{{ $t('book.title') }}</span>
        </h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <b-breadcrumb style="background-color: transparent;">
          <b-breadcrumb-item :to="{ name: 'index' }">{{ $t('index.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item>{{ $t('book.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item
            :to="applicable_books_route"
            v-if="group === 'applicable'"
          >{{ $t('book.list.application.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item
            :to="downloadable_books_route"
            v-if="group === 'downloadable'"
          >{{ $t('book.list.download.title') }}</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <b-nav pills fill>
          <b-nav-item
            :to="applicable_books_route"
            :active="group === 'applicable'"
          >{{ $t('book.list.application.title') }}</b-nav-item>
          <b-nav-item
            :to="downloadable_books_route"
            :active="group === 'downloadable'"
          >{{ $t('book.list.download.title') }}</b-nav-item>
          <b-nav-item :to="{ name: 'books-search' }">{{ $t('book.list.search.title') }}</b-nav-item>
        </b-nav>
      </div>
    </div>
    <div class="row mt-3" v-if="group == 'applicable'">
      <div class="col-12">
        <application-list-button-toolbar></application-list-button-toolbar>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <book-language-selector-button
          :languages="languages"
          @select="redirectToSpecificLanguageBooks"
        ></book-language-selector-button>
        <b-button
          variant="link"
          target="_blank"
          href="https://www2.budaedu.org/dharma-data/dharmas.xlsx"
          v-if="group == 'applicable'"
        >{{ $t('book.list.application.link.master-list-download') }}</b-button>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <h4>{{ $t('book.list.label.selected-language') }}：{{ $i18n.locale === 'tw' ? selected_language.chinese_name : selected_language.english_name }}</h4>
      </div>
    </div>
    <b-overlay :show="is_loading" rounded="sm" opacity="0.6">
      <div class="row mt-3">
        <div class="col-12">
          <b-form-select v-model="per_page" :options="per_page_options"></b-form-select>
        </div>
      </div>
      <book-card-list :books="books" :applicable="group === 'applicable'"></book-card-list>
      <template #overlay>
        <div class="text-center">
          <b-icon icon="arrow-clockwise" font-scale="5" animation="spin-pulse"></b-icon>
        </div>
      </template>
      <div class="row mt-3" v-if="group == 'applicable' && show_next_page_notice">
        <div class="col-12">
          <b-alert show class="text-center font-weight-bold mb-0 border-0" style="font-size: 1.75rem; background-color: #F7F4EF; color: #ff3030;">
            {{ $t('book.list.modal.next-page-notice.message') }}
          </b-alert>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <b-pagination
            size="lg"
            align="center"
            v-model="page"
            :total-rows="books_meta.total"
            :per-page="per_page"
            @change="pageChange"
          ></b-pagination>
        </div>
      </div>
    </b-overlay>
  </div>
</template>
<script>
import Vue from 'vue'

import { 
  BreadcrumbPlugin,
  ButtonGroupPlugin,
  ButtonPlugin,
  ButtonToolbarPlugin,
  FormSelectPlugin,
  IconsPlugin,
  ListGroupPlugin,
  ModalPlugin,
  NavPlugin,
  OverlayPlugin,
  PaginationPlugin
} from 'bootstrap-vue'

import BookLanguageSelectorButton from '@/components/buttons/BookLanguageSelectorButton.vue'
import ApplicationListButtonToolbar from '@/components/button-toolbars/application-list-button-toolbar/ApplicationListButtonToolbar'
import BookCardList from '@/components/lists/BookCardList'

import i18n from '@/plugins/i18n.js'

import BookApiService from '@/services/budaedu-api-services/book-api-service.js'
import BookLanguageApiService from '@/services/budaedu-api-services/book-language-api-service.js'

Vue.use(BreadcrumbPlugin)
Vue.use(ButtonGroupPlugin)
Vue.use(ButtonPlugin)
Vue.use(ButtonToolbarPlugin)
Vue.use(FormSelectPlugin)
Vue.use(IconsPlugin)
Vue.use(ListGroupPlugin)
Vue.use(ModalPlugin)
Vue.use(NavPlugin)
Vue.use(OverlayPlugin)
Vue.use(PaginationPlugin)

Vue.use(BookApiService)
Vue.use(BookLanguageApiService)

export default {
  i18n,
  name: 'books',
  props: {
    group: {
      type: String,
      required: true
    },
    language: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      is_loading: false,
      per_page: 30,
      page: 1,
      languages: [],
      selected_language: {},
      books: [],
      books_meta: { total: 0, current_page: null, last_page: null },
      book: {},
      book_cover: {},
      book_efiles: []
    }
  },
  computed: {
    applicable_books_route() {
      return {
        name: 'books',
        params: {
          group: 'applicable',
          language: (this.$i18n.locale === 'tw' ? 'chinese' : 'english')
        }
      }
    },
    downloadable_books_route() {
      return {
        name: 'books',
        params: {
          group: 'downloadable',
          language: (this.$i18n.locale === 'tw' ? 'chinese' : 'english')
        }
      }
    },
    per_page_options() {
      return [
        { value: 30, text: this.$t('paginator.items-per-page-selector.option.label', { size: 30 }) },
        { value: 50, text: this.$t('paginator.items-per-page-selector.option.label', { size: 50 }) },
        { value: 100, text: this.$t('paginator.items-per-page-selector.option.label', { size: 100 }) },
        { value: 200, text: this.$t('paginator.items-per-page-selector.option.label', { size: 200 }) }
      ]
    },
    show_next_page_notice() {
      return this.books_meta.last_page &&
        this.books_meta.current_page &&
        this.books_meta.last_page > this.books_meta.current_page &&
        this.books.filter(book => book.in_stock == 'Y').length == this.per_page 
    }
  },
  watch: {
    per_page(new_value, old_value) {
      this.page = 1
      this.setBooksAndPaging()
    }
  },
  methods: {
    redirectToSpecificLanguageBooks(language_english_name) {
      this.$router.push({
        name: 'books',
        params: {
          group: this.group,
          language: language_english_name.toLowerCase()
        }
      });
    },
    pageChange(page) {
      this.page = page
      this.setBooksAndPaging()
    },
    getBooksOrder() {
      switch (this.group) {
        case 'applicable':
          return 'latest_storage_date,desc|order_by_language_category_count,asc|code,desc'
        case 'downloadable':
          return 'order_by_language_category_count,asc|code,asc'
        default:
          return 'code,asc'
      }
    },
    async setBooksAndPaging() {
      this.is_loading = true
      this.books = []

      try {
        let items = {}
        let fron_cover = { url: 'img/common.jpg' }
        
        items = await this.$bookApiService.queryByLanguage(
          this.language,
          `filter[have_efile]&order=${this.getBooksOrder()}&per_page=${this.per_page}&page=${this.page}`
        )
        const previewPromises = items.data.map(async (book) => {
          book.front_cover = await this.$bookApiService.getFrontCover(book, fron_cover)
        })
        await Promise.all(previewPromises)

        this.books = items.data
        this.books_meta = items.meta
      } finally {
        this.is_loading = false
      }
    },
    async setLanguages() {
      let order = ''
      switch (this.$i18n.locale) {
        case 'tw':
          order = 'chinese_display_order,asc|english_name,asc'
          break
        case 'en':
          order = 'english_display_order,asc|english_name,asc'
          break
        default:
          order = 'english_display_order,asc|english_name,asc'
      }

      const items = await this.$bookLanguageApiService.queryTopLevel(`order=${order}`)
      this.languages = items.data
    }
  },
  async created() {
    // alert(`Vue version : ${Vue.version}`)
    await this.setLanguages()
    this.selected_language = this.languages.find(item => item.english_name.toLowerCase() == this.language)
    
    await this.setBooksAndPaging()
  },
  components: {
    ApplicationListButtonToolbar,
    BookCardList,
    BookLanguageSelectorButton
  },
  metaInfo() {
    let sub_title = ''
    switch (this.group) {
      case 'applicable':
        sub_title = this.$t('book.list.application.title')
        break
      case 'downloadable':
        sub_title = this.$t('book.list.download.title')
        break
      default:
        sub_title = ''
    }

    return {
      title: `${this.$t('book.title')}${sub_title}`,
      meta: [{
        vmid: 'description',
        name: 'description',
        content: ''
      }]
    }
  }
}
</script>
